import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Save, Check, Plus, Trash2, BookOpen, FileUp, Upload } from "lucide-react";
import { Card, CardBody, CardHeader } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { SlotsEditor, type SlotDraft } from "../components/SlotsEditor";
import { api, ApiError } from "../lib/api";
import type { IntentMatchingRule, IntentSummary } from "../lib/me";

const KNOWLEDGE_MAX = 5000;

export default function CategoryDetail() {
  const { intentId } = useParams();
  const [intent, setIntent] = useState<IntentSummary | null | undefined>(undefined);

  const load = async () => {
    if (!intentId) return;
    try {
      const data = await api.get<IntentSummary>(`/api/intents/${intentId}`);
      setIntent(data);
    } catch {
      setIntent(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentId]);

  if (intent === undefined) return <div className="text-sm text-gray-500">Loading…</div>;
  if (intent === null) return <div className="text-sm text-gray-500">Not found.</div>;

  return (
    <div className="space-y-6">
      <Link to="/categories" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Categories
      </Link>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{intent.displayName}</h1>
        <p className="mt-1 text-sm text-gray-500">{intent.description}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ReplyBehaviorCard intent={intent} onSaved={load} />

        <div className="space-y-6 lg:col-span-2">
          <IdentityCard intent={intent} onSaved={load} />
          <KnowledgeCard intent={intent} onSaved={load} />
          <ConnectorLinkCard intent={intent} onSaved={load} />
          <ServiceLinkCard intent={intent} onSaved={load} />
          <ExamplesCard intent={intent} onSaved={load} />
          <MatchingRulesCard intent={intent} onSaved={load} />
          <SlotsCard intent={intent} onSaved={load} />
        </div>
      </div>
    </div>
  );
}

/* ── Identity card (displayName + description) ─────────────────────────── */

function IdentityCard({ intent, onSaved }: { intent: IntentSummary; onSaved: () => void | Promise<void> }) {
  const [draft, setDraft] = useState({
    displayName: intent.displayName,
    description: intent.description,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft({ displayName: intent.displayName, description: intent.description });
  }, [intent.displayName, intent.description]);

  const dirty = draft.displayName !== intent.displayName || draft.description !== intent.description;

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.patch(`/api/intents/${intent.intentId}`, draft);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onSaved();
    } catch (err) {
      setError((err as ApiError).message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Identity" description="Name and description used by the classifier." />
      <CardBody className="space-y-4">
        <Input
          label="Display name"
          maxLength={60}
          value={draft.displayName}
          onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))}
        />
        <div>
          <label className="block text-sm font-medium text-gray-900">Description</label>
          <textarea
            rows={3}
            maxLength={500}
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
          <p className="mt-1 text-xs text-gray-500">{draft.description.length}/500</p>
        </div>
        {error && <ErrorRow message={error} />}
        <SaveRow saving={saving} saved={saved} disabled={!dirty} onClick={save} />
      </CardBody>
    </Card>
  );
}

/* ── Knowledge card ────────────────────────────────────────────────────── */

function KnowledgeCard({ intent, onSaved }: { intent: IntentSummary; onSaved: () => void | Promise<void> }) {
  const [content, setContent] = useState(intent.knowledgeContent ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setContent(intent.knowledgeContent ?? "");
  }, [intent.knowledgeContent]);

  const dirty = content !== (intent.knowledgeContent ?? "");
  const remaining = KNOWLEDGE_MAX - content.length;

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.patch(`/api/intents/${intent.intentId}`, { knowledgeContent: content });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onSaved();
    } catch (err) {
      setError((err as ApiError).message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-gray-400" />
            Knowledge / Reference material
          </span>
        }
        description={
          <>
            FAQ-style facts the AI uses when drafting replies for this category. The reply LLM
            treats this as authoritative — anything outside is "I'll escalate".
          </>
        }
      />
      <CardBody className="space-y-3">
        <textarea
          rows={10}
          value={content}
          maxLength={KNOWLEDGE_MAX}
          onChange={(e) => setContent(e.target.value)}
          placeholder={`Examples of useful content:\n\n• Refund policy: full refund within 30 days, partial after.\n• Plan limits: Starter = 1k emails/mo, Growth = 10k, Enterprise = unlimited.\n• Password reset link: yourapp.com/forgot — expires after 1 hour.\n\nKeep entries short and factual. Bullet points + 1-line answers work best.`}
          className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
        />
        <div className="flex items-baseline justify-between text-xs">
          <p className="text-gray-500">
            Tip: prefer bullet points over paragraphs. Concise facts produce better replies and
            stretch the {KNOWLEDGE_MAX.toLocaleString()}-character budget further.
          </p>
          <span className={remaining < 200 ? "font-medium text-amber-700" : "text-gray-500"}>
            {content.length.toLocaleString()}/{KNOWLEDGE_MAX.toLocaleString()}
          </span>
        </div>
        {error && <ErrorRow message={error} />}
        <SaveRow saving={saving} saved={saved} disabled={!dirty} onClick={save} />

        {/* Coming-soon teaser for RAG-backed file uploads */}
        <div className="mt-2 rounded-md border border-dashed border-gray-200 bg-gray-50 p-4">
          <div className="flex items-start gap-3">
            <FileUp className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                Upload PDF or Markdown files
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  Coming soon
                </span>
                <span className="ml-2 inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                  Growth + Enterprise
                </span>
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Drop in your runbooks, FAQ docs, or vendor manuals as PDF or .md files. Triage will index
                them and retrieve the relevant passages when drafting replies — no more squeezing
                everything into the 5,000-character text field above.
              </p>
              <button
                type="button"
                disabled
                className="mt-3 inline-flex cursor-not-allowed items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-400"
              >
                <Upload className="h-3.5 w-3.5" />
                Choose files…
              </button>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/* ── Examples card ─────────────────────────────────────────────────────── */

function ExamplesCard({ intent, onSaved }: { intent: IntentSummary; onSaved: () => void | Promise<void> }) {
  const [examples, setExamples] = useState(intent.examples);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setExamples(intent.examples);
  }, [intent.examples]);

  const dirty = JSON.stringify(examples) !== JSON.stringify(intent.examples);
  const valid = examples.filter((e) => e.subject.trim() && e.snippet.trim()).length >= 3;

  const update = (i: number, patch: Partial<{ subject: string; snippet: string }>) =>
    setExamples((arr) => arr.map((ex, idx) => (idx === i ? { ...ex, ...patch } : ex)));

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.patch(`/api/intents/${intent.intentId}`, { examples });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onSaved();
    } catch (err) {
      setError((err as ApiError).message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={`Examples (${examples.length})`}
        description="Few-shot guidance for the AI classifier. Minimum 3."
        actions={
          <Button size="sm" variant="secondary" onClick={() => setExamples((a) => [...a, { subject: "", snippet: "" }])}>
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        }
      />
      <CardBody className="space-y-4">
        {examples.map((ex, i) => (
          <div key={i} className="space-y-2 rounded-md border border-gray-200 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Example {i + 1}
              </span>
              {examples.length > 3 && (
                <button
                  type="button"
                  onClick={() => setExamples((a) => a.filter((_, idx) => idx !== i))}
                  className="text-gray-400 hover:text-rose-600 cursor-pointer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <Input
              label="Subject"
              value={ex.subject}
              onChange={(e) => update(i, { subject: e.target.value })}
            />
            <div>
              <label className="block text-sm font-medium text-gray-900">Snippet</label>
              <textarea
                rows={2}
                value={ex.snippet}
                onChange={(e) => update(i, { snippet: e.target.value })}
                className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
              />
            </div>
          </div>
        ))}
        {error && <ErrorRow message={error} />}
        <SaveRow saving={saving} saved={saved} disabled={!dirty || !valid} onClick={save} />
      </CardBody>
    </Card>
  );
}

/* ── Matching rules card ───────────────────────────────────────────────── */

const RULE_TYPES: Array<{ value: IntentMatchingRule["type"]; label: string; isList?: boolean }> = [
  { value: "subject_contains", label: "Subject contains" },
  { value: "body_contains", label: "Body contains" },
  { value: "subject_regex", label: "Subject matches regex" },
  { value: "body_regex", label: "Body matches regex" },
  { value: "from_domain_in", label: "From domain in", isList: true },
  { value: "from_email_in", label: "From email in", isList: true },
];

function MatchingRulesCard({ intent, onSaved }: { intent: IntentSummary; onSaved: () => void | Promise<void> }) {
  const [rules, setRules] = useState<IntentMatchingRule[]>(intent.matchingRules);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRules(intent.matchingRules);
  }, [intent.matchingRules]);

  const dirty = JSON.stringify(rules) !== JSON.stringify(intent.matchingRules);

  const update = (i: number, patch: Partial<IntentMatchingRule>) =>
    setRules((arr) => arr.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      // Convert array-typed rules' string input to actual array
      const cleaned = rules.map((r) => {
        if (r.type === "from_domain_in" || r.type === "from_email_in") {
          const v = Array.isArray(r.value) ? r.value : (typeof r.value === "string" ? r.value.split(",") : []);
          return { ...r, value: v.map((s) => String(s).trim()).filter(Boolean) };
        }
        return r;
      });
      await api.patch(`/api/intents/${intent.intentId}`, { matchingRules: cleaned });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onSaved();
    } catch (err) {
      setError((err as ApiError).message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={`Matching rules (${rules.length})`}
        description="Run before the LLM. If all rules match, classification is deterministic and skips Bedrock."
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setRules((r) => [...r, { type: "subject_contains", value: "" }])}
            disabled={rules.length >= 10}
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        }
      />
      <CardBody className="space-y-3">
        {rules.length === 0 && (
          <p className="text-sm text-gray-500">
            No rules — every classification goes to the LLM. Rules are optional but speed up
            obvious matches and save Bedrock tokens.
          </p>
        )}
        {rules.map((rule, i) => {
          const meta = RULE_TYPES.find((t) => t.value === rule.type);
          const valueAsString = Array.isArray(rule.value) ? rule.value.join(", ") : (rule.value ?? "");
          return (
            <div key={i} className="flex items-start gap-2 rounded-md border border-gray-200 p-3">
              <select
                value={rule.type}
                onChange={(e) => update(i, { type: e.target.value as IntentMatchingRule["type"], value: meta?.isList ? [] : "" })}
                className="rounded-md border border-gray-200 bg-white px-2 py-2 text-sm"
              >
                {RULE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <input
                type="text"
                value={valueAsString}
                placeholder={meta?.isList ? "comma, separated, list" : "value"}
                onChange={(e) => update(i, { value: e.target.value })}
                className="flex-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => setRules((r) => r.filter((_, idx) => idx !== i))}
                className="self-stretch px-2 text-gray-400 hover:text-rose-600 cursor-pointer"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
        {error && <ErrorRow message={error} />}
        <SaveRow saving={saving} saved={saved} disabled={!dirty} onClick={save} />
      </CardBody>
    </Card>
  );
}

/* ── Slots (editable) ──────────────────────────────────────────────── */

function SlotsCard({ intent, onSaved }: { intent: IntentSummary; onSaved: () => void | Promise<void> }) {
  const initial: SlotDraft[] = (intent.slots ?? []).map((s) => ({
    name: s.name,
    label: s.label,
    type: (s.type as SlotDraft["type"]) ?? "string",
    required: s.required,
    requiredGroup: s.requiredGroup,
    extractionHint: (s as unknown as { extractionHint?: string }).extractionHint ?? "",
    privacyClass: ((s as unknown as { privacyClass?: SlotDraft["privacyClass"] }).privacyClass) ?? "non_pii",
    validationRegex: (s as unknown as { validationRegex?: string }).validationRegex,
    normalizer: (s as unknown as { normalizer?: string }).normalizer,
    enumValues: (s as unknown as { enumValues?: string[] }).enumValues,
  }));

  const [slots, setSlots] = useState<SlotDraft[]>(initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSlots(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent.intentId, JSON.stringify(intent.slots)]);

  const dirty = JSON.stringify(slots) !== JSON.stringify(initial);

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.patch(`/api/intents/${intent.intentId}`, { slots });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onSaved();
    } catch (err) {
      setError((err as ApiError).message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={`Required information (${slots.length})`}
        description="Slots tell the AI what info this category needs. The classifier extracts what it can from the email; the gather step asks the customer for anything missing required."
      />
      <CardBody className="space-y-4">
        <SlotsEditor slots={slots} onChange={setSlots} error={error ?? undefined} />
        <SaveRow saving={saving} saved={saved} disabled={!dirty} onClick={save} />
      </CardBody>
    </Card>
  );
}

/* ── Reply behavior card (existing — unchanged behavior) ──────────────── */

function ReplyBehaviorCard({
  intent,
  onSaved,
}: {
  intent: IntentSummary;
  onSaved: () => void | Promise<void>;
}) {
  const [draft, setDraft] = useState({
    replyMode: intent.replyMode,
    confidenceThreshold: intent.confidenceThreshold,
    autoSendThreshold: intent.autoSendThreshold,
    gatherMaxAttempts: intent.gatherMaxAttempts ?? 2,
    priority: intent.priority,
    status: intent.status,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft({
      replyMode: intent.replyMode,
      confidenceThreshold: intent.confidenceThreshold,
      autoSendThreshold: intent.autoSendThreshold,
      gatherMaxAttempts: intent.gatherMaxAttempts ?? 2,
      priority: intent.priority,
      status: intent.status,
    });
  }, [intent.replyMode, intent.confidenceThreshold, intent.autoSendThreshold, intent.gatherMaxAttempts, intent.priority, intent.status]);

  const dirty =
    draft.replyMode !== intent.replyMode ||
    draft.confidenceThreshold !== intent.confidenceThreshold ||
    draft.autoSendThreshold !== intent.autoSendThreshold ||
    draft.gatherMaxAttempts !== (intent.gatherMaxAttempts ?? 2) ||
    draft.priority !== intent.priority ||
    draft.status !== intent.status;

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.patch(`/api/intents/${intent.intentId}`, draft);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onSaved();
    } catch (err) {
      setError((err as ApiError).message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Reply behavior" description="Controls how this category routes through the AI pipeline." />
      <CardBody className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-900">Reply mode</label>
          <select
            value={draft.replyMode}
            onChange={(e) => setDraft((d) => ({ ...d, replyMode: e.target.value as "direct" | "draft" }))}
            className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            <option value="draft">Draft (admin reviews)</option>
            <option value="direct">Direct (auto-send)</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <NumberField
            label="Confidence threshold"
            hint="Below = review."
            value={draft.confidenceThreshold}
            onChange={(v) => setDraft((d) => ({ ...d, confidenceThreshold: v }))}
          />
          <NumberField
            label="Auto-send threshold"
            hint="At/above + Direct = auto-send."
            value={draft.autoSendThreshold}
            onChange={(v) => setDraft((d) => ({ ...d, autoSendThreshold: v }))}
          />
        </div>

        <div>
          <Input
            label="Max gather attempts"
            type="number"
            min={0}
            max={5}
            value={draft.gatherMaxAttempts}
            onChange={(e) => setDraft((d) => ({
              ...d,
              gatherMaxAttempts: Math.max(0, Math.min(5, Number(e.target.value) || 0)),
            }))}
            hint="How many times AI will email the customer asking for missing slot info before escalating. 0 disables gather entirely — the case escalates the moment a required slot is missing. 2–3 is the typical setting."
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Priority"
            type="number"
            min={1}
            max={999}
            value={draft.priority}
            onChange={(e) => setDraft((d) => ({ ...d, priority: Number(e.target.value) || 50 }))}
          />
          <div>
            <label className="block text-sm font-medium text-gray-900">Status</label>
            <select
              value={draft.status}
              onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as "active" | "draft" | "archived" }))}
              className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
            >
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {error && <ErrorRow message={error} />}
        <SaveRow saving={saving} saved={saved} disabled={!dirty} onClick={save} />
      </CardBody>
    </Card>
  );
}

/* ── small helpers ────────────────────────────────────────────────────── */

function NumberField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <Input
      label={label}
      type="number"
      step="0.05"
      min={0}
      max={1}
      value={value}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (!Number.isNaN(n)) onChange(Math.max(0, Math.min(1, n)));
      }}
      hint={hint}
    />
  );
}

function ErrorRow({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {message}
    </div>
  );
}

function SaveRow({
  saving,
  saved,
  disabled,
  onClick,
}: {
  saving: boolean;
  saved: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-3 pt-1">
      {saved && (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
          <Check className="h-3.5 w-3.5" /> Saved
        </span>
      )}
      <Button size="sm" onClick={onClick} loading={saving} disabled={disabled}>
        <Save className="h-3.5 w-3.5" /> Save
      </Button>
    </div>
  );
}

// keep this import alive for ReactNode use in some sections
void (null as ReactNode);

/* ── Connector link card ─────────────────────────────────────────────── */

interface ConnectorChoice {
  connectorId: string;
  displayName: string;
  method: "GET" | "POST";
  baseUrl: string;
}

function ConnectorLinkCard({ intent, onSaved }: { intent: IntentSummary; onSaved: () => void | Promise<void> }) {
  const [linked, setLinked] = useState<string | null>(intent.connectorId ?? null);
  const [choices, setChoices] = useState<ConnectorChoice[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLinked(intent.connectorId ?? null);
  }, [intent.connectorId]);

  useEffect(() => {
    api.get<ConnectorChoice[]>("/api/connectors").then(setChoices).catch(() => setChoices([]));
  }, []);

  const dirty = (linked ?? "") !== (intent.connectorId ?? "");

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.patch(`/api/intents/${intent.intentId}`, {
        connectorId: linked === null || linked === "" ? null : linked,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onSaved();
    } catch (err) {
      setError((err as ApiError).message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const selected = choices?.find((c) => c.connectorId === linked) ?? null;

  return (
    <Card>
      <CardHeader
        title="Linked connector"
        description="When set, Triage calls this HTTP endpoint after gathering required slots and injects the response into the reply prompt as authoritative live data."
      />
      <CardBody className="space-y-3">
        {choices === null ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : choices.length === 0 ? (
          <p className="text-sm text-gray-500">
            No connectors defined yet.{" "}
            <Link to="/connectors/new" className="text-gray-900 underline">Create one</Link> to link it here.
          </p>
        ) : (
          <>
            <select
              value={linked ?? ""}
              onChange={(e) => setLinked(e.target.value || null)}
              className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
            >
              <option value="">— no connector —</option>
              {choices.map((c) => (
                <option key={c.connectorId} value={c.connectorId}>
                  {c.displayName} ({c.method} {hostOf(c.baseUrl)})
                </option>
              ))}
            </select>
            {selected && (
              <p className="text-xs text-gray-500">
                <Link to={`/connectors/${selected.connectorId}`} className="text-gray-900 hover:underline">
                  Open connector
                </Link>{" "}
                — edit the request signature, secret, or test it.
              </p>
            )}
            <SaveRow saving={saving} saved={saved} disabled={!dirty} onClick={save} />
            {error && <p className="text-sm text-rose-600">{error}</p>}
          </>
        )}
      </CardBody>
    </Card>
  );
}

function hostOf(url: string): string {
  try { return new URL(url).host; } catch { return url; }
}

/* ── Service link card ────────────────────────────────────────────────── */

interface ServiceChoice {
  serviceId: string;
  displayName: string;
  serviceType: string;
  region: string;
}

function ServiceLinkCard({ intent, onSaved }: { intent: IntentSummary; onSaved: () => void | Promise<void> }) {
  const [linked, setLinked] = useState<string | null>(intent.serviceId ?? null);
  const [choices, setChoices] = useState<ServiceChoice[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLinked(intent.serviceId ?? null);
  }, [intent.serviceId]);

  useEffect(() => {
    api.get<ServiceChoice[]>("/api/services").then(setChoices).catch(() => setChoices([]));
  }, []);

  const dirty = (linked ?? "") !== (intent.serviceId ?? "");

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.patch(`/api/intents/${intent.intentId}`, {
        serviceId: linked === null || linked === "" ? null : linked,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onSaved();
    } catch (err) {
      setError((err as ApiError).message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const selected = choices?.find((s) => s.serviceId === linked) ?? null;

  return (
    <Card>
      <CardHeader
        title="Linked external service"
        description="Link an external service connection like Amazon S3. If an S3 file link is found in the drafted reply, Triage will download the file using these credentials and attach it to the email reply."
      />
      <CardBody className="space-y-3">
        {choices === null ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : choices.length === 0 ? (
          <p className="text-sm text-gray-500">
            No external services connected yet.{" "}
            <Link to="/services" className="text-gray-900 underline">Connect a service</Link> to link it here.
          </p>
        ) : (
          <>
            <select
              value={linked ?? ""}
              onChange={(e) => setLinked(e.target.value || null)}
              className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
            >
              <option value="">— no external service —</option>
              {choices.map((s) => (
                <option key={s.serviceId} value={s.serviceId}>
                  {s.displayName} ({s.serviceType.toUpperCase()} in {s.region})
                </option>
              ))}
            </select>
            {selected && (
              <p className="text-xs text-gray-500">
                <Link to="/services" className="text-gray-900 hover:underline">
                  Manage services
                </Link>{" "}
                — edit AWS credentials, region, or test S3 bucket connection.
              </p>
            )}
            <SaveRow saving={saving} saved={saved} disabled={!dirty} onClick={save} />
            {error && <p className="text-sm text-rose-600">{error}</p>}
          </>
        )}
      </CardBody>
    </Card>
  );
}
